import { ToolItem } from '../types';

export interface DataToolItem extends ToolItem {
  dataCategory: 'json' | 'csv-tabular' | 'xml-markup' | 'sql-formatting' | 'markdown-diff';
  dataCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const DATA_CATEGORIES_TABS = [
  { id: 'all', label: 'All Data Tools', icon: 'Database' },
  { id: 'json', label: 'JSON Tools & Converters', icon: 'FileJson' },
  { id: 'csv-tabular', label: 'CSV, TSV & Data Tables', icon: 'Table' },
  { id: 'xml-markup', label: 'XML & Markup Utilities', icon: 'Code' },
  { id: 'sql-formatting', label: 'SQL Tools & Minifiers', icon: 'Database' },
  { id: 'markdown-diff', label: 'Markdown & Diff Tools', icon: 'GitCompare' }
];

export const DATA_TOOLS_DATA: DataToolItem[] = [
  // --- JSON TOOLS & CONVERTERS (12 Tools) ---
  {
    id: 'json-formatter',
    name: 'JSON Formatter',
    description: 'Prettify, format, and indent raw JSON data strings with custom indentation levels.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'FileJson',
    isPopular: true,
    actionType: 'json'
  },
  {
    id: 'json-validator',
    name: 'JSON Validator',
    description: 'Validate JSON syntax structure and pinpoint line-by-line syntax error positions.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'CheckCircle2',
    isPopular: true,
    actionType: 'json'
  },
  {
    id: 'json-minifier',
    name: 'JSON Minifier',
    description: 'Compress and minify JSON payload sizes by stripping extra whitespace, tabs, and newlines.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Minimize2',
    actionType: 'json'
  },
  {
    id: 'json-beautifier',
    name: 'JSON Beautifier',
    description: 'Transform ugly minified JSON objects into syntax-highlighted, clean readable code.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Sparkles',
    actionType: 'json'
  },
  {
    id: 'json-compare',
    name: 'JSON Compare',
    description: 'Compare two JSON objects side-by-side to detect missing keys, value differences, and structural changes.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'GitCompare',
    isPopular: true,
    actionType: 'json'
  },
  {
    id: 'json-diff',
    name: 'JSON Diff',
    description: 'Generate patch diffs showing additions, deletions, and updates between JSON objects.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'FileDiff',
    actionType: 'json'
  },
  {
    id: 'json-tree-viewer',
    name: 'JSON Tree Viewer',
    description: 'Interactive collapsible tree browser for navigating complex nested JSON structures.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'FolderTree',
    isPopular: true,
    actionType: 'json'
  },
  {
    id: 'json-path-tester',
    name: 'JSON Path Tester',
    description: 'Test JSONPath queries against JSON data to filter and extract deeply nested elements.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Search',
    actionType: 'json'
  },
  {
    id: 'json-to-csv',
    name: 'JSON to CSV',
    description: 'Convert JSON arrays or nested API responses into flat CSV spreadsheet files.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'ArrowRightLeft',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'csv-to-json',
    name: 'CSV to JSON',
    description: 'Parse CSV spreadsheets and TSV files into clean JSON object arrays.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'ArrowRightLeft',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'json-to-yaml',
    name: 'JSON to YAML',
    description: 'Convert JSON objects into clean YAML configuration file syntax.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'FileCode',
    actionType: 'generic'
  },
  {
    id: 'yaml-to-json',
    name: 'YAML to JSON',
    description: 'Parse YAML configurations into structured JSON data models.',
    dataCategory: 'json',
    dataCategoryLabel: 'JSON Tools & Converters',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'FileCode',
    actionType: 'generic'
  },

  // --- CSV, TSV & DATA TABLES (9 Tools) ---
  {
    id: 'csv-viewer',
    name: 'CSV Viewer',
    description: 'Open, search, filter, and view large CSV files with an interactive grid data view.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Table',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'csv-editor',
    name: 'CSV Editor',
    description: 'Inline edit rows, add columns, modify headers, and export modified CSV files.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Edit3',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'csv-splitter',
    name: 'CSV Splitter',
    description: 'Split massive CSV files into multiple smaller CSV files by row count or file size limits.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Scissors',
    actionType: 'generic'
  },
  {
    id: 'csv-merger',
    name: 'CSV Merger',
    description: 'Combine and merge multiple CSV spreadsheets into a single unified dataset.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Combine',
    actionType: 'generic'
  },
  {
    id: 'csv-sorter',
    name: 'CSV Sorter',
    description: 'Sort CSV datasets alphabetically or numerically by multiple selected column keys.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'ArrowUpDown',
    actionType: 'generic'
  },
  {
    id: 'tsv-viewer',
    name: 'TSV Viewer',
    description: 'Inspect tab-separated value (.tsv) files with automatic delimiter detection and table grids.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'FileSpreadsheet',
    actionType: 'generic'
  },
  {
    id: 'data-table-viewer',
    name: 'Data Table Viewer',
    description: 'High-performance virtualized grid for analyzing tabular data, CSVs, and array datasets.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'LayoutGrid',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'table-generator',
    name: 'Table Generator',
    description: 'Create customized Markdown, HTML, and LaTeX data tables with instant code previews.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Grid',
    actionType: 'generic'
  },
  {
    id: 'dataset-statistics',
    name: 'Dataset Statistics',
    description: 'Calculate summary statistics (mean, median, null counts, standard deviation) for columns in CSV data.',
    dataCategory: 'csv-tabular',
    dataCategoryLabel: 'CSV, TSV & Data Tables',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'BarChart2',
    actionType: 'generic'
  },

  // --- XML & MARKUP UTILITIES (5 Tools) ---
  {
    id: 'xml-formatter',
    name: 'XML Formatter',
    description: 'Prettify and indent XML documents with customizable tag spacing and attributes.',
    dataCategory: 'xml-markup',
    dataCategoryLabel: 'XML & Markup Utilities',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Code',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'xml-validator',
    name: 'XML Validator',
    description: 'Check XML well-formedness and validate tags against XML schema or DTD specifications.',
    dataCategory: 'xml-markup',
    dataCategoryLabel: 'XML & Markup Utilities',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'CheckCircle2',
    actionType: 'generic'
  },
  {
    id: 'xml-beautifier',
    name: 'XML Beautifier',
    description: 'Format raw XML documents into beautiful, color-coded, syntax-highlighted markup.',
    dataCategory: 'xml-markup',
    dataCategoryLabel: 'XML & Markup Utilities',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },
  {
    id: 'xml-minifier',
    name: 'XML Minifier',
    description: 'Strip XML comments, redundant spaces, and trailing linebreaks to reduce payload size.',
    dataCategory: 'xml-markup',
    dataCategoryLabel: 'XML & Markup Utilities',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Minimize2',
    actionType: 'generic'
  },
  {
    id: 'data-uri-generator',
    name: 'Data URI Generator',
    description: 'Convert files, SVG graphics, and images into Base64 inline Data URI strings (`data:image/...`).',
    dataCategory: 'xml-markup',
    dataCategoryLabel: 'XML & Markup Utilities',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Link',
    isPopular: true,
    actionType: 'generic'
  },

  // --- SQL TOOLS & MINIFIERS (3 Tools) ---
  {
    id: 'sql-formatter',
    name: 'SQL Formatter',
    description: 'Format complex SQL queries with standardized keyword capitalization and clean indentation.',
    dataCategory: 'sql-formatting',
    dataCategoryLabel: 'SQL Tools & Minifiers',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Database',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'sql-beautifier',
    name: 'SQL Beautifier',
    description: 'Prettify SQL statements for PostgreSQL, MySQL, SQLite, Oracle, and MS SQL dialects.',
    dataCategory: 'sql-formatting',
    dataCategoryLabel: 'SQL Tools & Minifiers',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },
  {
    id: 'sql-minifier',
    name: 'SQL Minifier',
    description: 'Compress SQL query strings into single-line statements for inline code embedded execution.',
    dataCategory: 'sql-formatting',
    dataCategoryLabel: 'SQL Tools & Minifiers',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Minimize2',
    actionType: 'generic'
  },

  // --- MARKDOWN & DIFF TOOLS (6 Tools) ---
  {
    id: 'markdown-editor',
    name: 'Markdown Editor',
    description: 'Distraction-free Markdown editor with live preview, toolbar formatting, and HTML export.',
    dataCategory: 'markdown-diff',
    dataCategoryLabel: 'Markdown & Diff Tools',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'FileText',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'markdown-preview',
    name: 'Markdown Previewer',
    description: 'Instant live HTML rendering of Markdown (.md) documents with syntax highlighting.',
    dataCategory: 'markdown-diff',
    dataCategoryLabel: 'Markdown & Diff Tools',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Eye',
    actionType: 'generic'
  },
  {
    id: 'markdown-to-html',
    name: 'Markdown to HTML',
    description: 'Convert Markdown strings directly into sanitized, clean HTML code snippets.',
    dataCategory: 'markdown-diff',
    dataCategoryLabel: 'Markdown & Diff Tools',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'html-to-markdown',
    name: 'HTML to Markdown',
    description: 'Convert HTML web pages and markup snippets into clean Markdown format.',
    dataCategory: 'markdown-diff',
    dataCategoryLabel: 'Markdown & Diff Tools',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'FileCode',
    actionType: 'generic'
  },
  {
    id: 'diff-checker',
    name: 'Diff Checker',
    description: 'Side-by-side text and code diff comparison highlighting additions, deletions, and line changes.',
    dataCategory: 'markdown-diff',
    dataCategoryLabel: 'Markdown & Diff Tools',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'GitCompare',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'text-compare',
    name: 'Text Compare',
    description: 'Compare two text blocks character-by-character or word-by-word with instant diff highlighting.',
    dataCategory: 'markdown-diff',
    dataCategoryLabel: 'Markdown & Diff Tools',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'FileDiff',
    actionType: 'generic'
  }
];
